import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TiimeAlertModule, TiimeCheckboxCardModule, TiimeTwocolorsIconModule } from 'tiime-material';

import { UserInvitationChannelType } from '@enums/user-invitation-channel-type.enum';
import { noop } from '@helpers/noop.helper';
import { OnChangeFn } from '@modules/core/types/on-change-fn';
import { OnTouchFn } from '@modules/core/types/on-touch-fn';
import { IsMobilePhoneNumberPipe } from '@shared-pipes/is-mobile-phone-number.pipe';

@Component({
  selector: 'app-invitation-channel-select',
  standalone: true,
  imports: [TiimeTwocolorsIconModule, TiimeCheckboxCardModule, TiimeAlertModule, FormsModule],
  templateUrl: './invitation-channel-select.component.html',
  styleUrls: ['./invitation-channel-select.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: InvitationChannelSelectComponent, multi: true }],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvitationChannelSelectComponent implements ControlValueAccessor {
  readonly displayTitle: InputSignal<boolean> = input<boolean>(true);
  readonly displayIcons: InputSignal<boolean> = input<boolean>(true);
  readonly userPhone: InputSignal<string | null> = input<string | null>(null);

  private readonly isMobilePhoneNumberPipe = new IsMobilePhoneNumberPipe();

  readonly email: WritableSignal<boolean> = signal<boolean>(false);
  readonly sms: WritableSignal<boolean> = signal<boolean>(false);
  readonly disabled: WritableSignal<boolean> = signal<boolean>(false);
  readonly isMobilePhoneNumber: Signal<boolean> = computed(() =>
    this.isMobilePhoneNumberPipe.transform(this.userPhone())
  );
  readonly disablePhoneInput: Signal<boolean> = computed(() => {
    const disabled = this.disabled();
    const isNotMobilePhoneNumber = !this.isMobilePhoneNumber();

    return disabled || isNotMobilePhoneNumber;
  });

  protected onChange: OnChangeFn<UserInvitationChannelType[]> = noop;
  protected onTouched: OnTouchFn = noop;

  registerOnChange(fn: OnChangeFn<UserInvitationChannelType[]>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  writeValue(obj: UserInvitationChannelType[]): void {
    this.email.set((obj ?? []).includes(UserInvitationChannelType.email));
    this.sms.set((obj ?? []).includes(UserInvitationChannelType.sms));
  }

  toggleChannel(): void {
    this.onChange(
      [this.email() && UserInvitationChannelType.email, this.sms() && UserInvitationChannelType.sms].filter(Boolean)
    );
    this.onTouched();
  }
}
